/**
 * Token recién creado: se muestra UNA sola vez, con botón «Copiar token».
 *
 * El valor vive solo en las props (estado de la página): no se guarda en localStorage, ni en la URL,
 * ni se escribe en logs. Si el portapapeles no está disponible o falla, se selecciona el texto
 * (Selection API) para que la persona use Ctrl/Cmd+C, y se avisa con `role="alert"`.
 */
import { useEffect, useRef, useState } from 'react';
import './TokenNuevo.css';

const VOLVER_A_COPIAR_MS = 2500;

type EstadoCopia = 'inicial' | 'copiado' | 'fallo';

export function TokenNuevo({ token, alCerrar }: { token: string; alCerrar: () => void }) {
  const [estado, setEstado] = useState<EstadoCopia>('inicial');
  const codigo = useRef<HTMLElement>(null);
  const temporizador = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(temporizador.current), []);

  const seleccionarTexto = () => {
    const nodo = codigo.current;
    const seleccion = window.getSelection();
    if (!nodo || !seleccion) return;
    const rango = document.createRange();
    rango.selectNodeContents(nodo);
    seleccion.removeAllRanges();
    seleccion.addRange(rango);
  };

  const copiar = async () => {
    window.clearTimeout(temporizador.current);
    try {
      // `navigator.clipboard` no existe fuera de contextos seguros (https / localhost).
      await navigator.clipboard.writeText(token);
      setEstado('copiado');
      temporizador.current = window.setTimeout(() => setEstado('inicial'), VOLVER_A_COPIAR_MS);
    } catch {
      seleccionarTexto();
      setEstado('fallo');
    }
  };

  return (
    <div className="token-nuevo">
      <strong>Copia tu token ahora: no se volverá a mostrar.</strong>
      <code ref={codigo}>{token}</code>
      <div className="token-nuevo__acciones">
        <button
          type="button"
          className="boton boton--primario token-nuevo__boton"
          onClick={() => void copiar()}
        >
          {estado === 'copiado' ? 'Copiado' : 'Copiar token'}
        </button>
        <button type="button" className="boton token-nuevo__boton" onClick={alCerrar}>
          Ya lo copié
        </button>
      </div>
      <p className="solo-lectores" aria-live="polite">
        {estado === 'copiado' ? 'Token copiado al portapapeles' : ''}
      </p>
      {estado === 'fallo' && (
        <p role="alert" className="token-nuevo__error">
          No pudimos copiarlo automáticamente. El token quedó seleccionado: presiona Ctrl+C (Cmd+C
          en Mac) para copiarlo.
        </p>
      )}
    </div>
  );
}
